import type { ReactNode } from "react";
import { IncidentApprovalTransition, IncidentResponderKind } from "@no-busy-work/ops-bridge-protocol";
import type { IncidentDetail } from "@no-busy-work/ops-bridge-protocol";
import { Button } from "@no-busy-work/ui";
import { BUTTON_TYPE, CLOSEOUT_GATE, GRAPH_NODE_KIND, OPS_ACTION_ENDPOINT_KIND, OUTAGE_NOTICE_STATE, UI_TONE, WORKFLOW_STATUS } from "../OpsCenter.Config";
import { accountabilityProjection, actionRows, activeTaskProjection, actorRefLabel, formatTime, impactGraph, lifecycleRows, responderRows, serviceLabel, severityTone, slaLabel, statusTone } from "../OpsCenter.Data";
import type { ActionRow, OpsCenterActions, OpsCenterCopy, ResponderRow, UiTone } from "../OpsCenter.Types";
import { humanize, statusLabel } from "../i18n/OpsCenter.I18n";
import { SectionHeader, StatusPill } from "./OpsCenterScreen.Shared";

interface IncidentCommandSurfaceProps {
  readonly actions: OpsCenterActions;
  readonly busy: boolean;
  readonly copy: OpsCenterCopy;
  readonly detail: IncidentDetail;
}

export function IncidentCommandSurface({ actions, busy, copy, detail }: IncidentCommandSurfaceProps): ReactNode {
  return (
    <section className="incident-command-surface" aria-label={copy.labels.currentIncident}>
      <IncidentCommandHeader copy={copy} detail={detail} />
      <IncidentSummaryGrid actions={actions} busy={busy} copy={copy} detail={detail} />
      <IncidentLifecycleStrip copy={copy} detail={detail} />
    </section>
  );
}

export function IncidentCommandHeader({ copy, detail }: {
  readonly copy: OpsCenterCopy;
  readonly detail: IncidentDetail;
}): ReactNode {
  const incident = detail.incident;
  const accountability = accountabilityProjection(detail, copy);
  return (
    <>
      <div className="incident-heading command-heading">
        <div className="command-heading-copy">
          <p className="eyebrow">{incident.id}</p>
          <h2>{serviceLabel(incident.serviceId, copy)}</h2>
        </div>
        <div className="status-row">
          <StatusPill label={incident.severity.toUpperCase()} tone={severityTone(incident.severity)} />
          <StatusPill label={statusLabel(copy, incident.status)} tone={statusTone(incident.status)} />
          <StatusPill label={humanize(incident.confidence)} tone={UI_TONE.Neutral} />
        </div>
      </div>
      <dl className="command-facts-strip">
        <CommandStripItem label={copy.forms.app} value={appLabel(incident.serviceId)} />
        <CommandStripItem label={copy.forms.environment} value={humanize(incident.environment)} />
        <CommandStripItem label={copy.labels.sla} value={slaLabel(detail, copy)} />
        <CommandStripItem label={copy.labels.status} value={statusLabel(copy, incident.status)} />
        <CommandStripItem label={copy.labels.lastUpdated} value={formatTime(incident.updatedAt)} />
      </dl>
      <dl className="command-owner-strip">
        <CommandStripItem label={copy.labels.serviceOwner} value={accountability.serviceOwner} />
        <CommandStripItem label={copy.labels.incidentCommander} value={accountability.incidentCommander} />
        <CommandStripItem label={copy.labels.communicationsOwner} value={accountability.communicationsOwner} />
        <CommandStripItem label={copy.labels.currentAssignee} value={accountability.currentAssignee} />
        <CommandStripItem label={copy.labels.handoffState} value={accountability.handoffState} />
      </dl>
    </>
  );
}

export function IncidentSummaryGrid({ actions, busy, copy, detail }: IncidentCommandSurfaceProps): ReactNode {
  const blocker = currentBlocker(detail, copy);
  const topology = topologySummary(detail, copy);
  return (
    <div className="command-summary-grid compact-command-grid">
      <CommandSummaryCard title={copy.labels.currentBlocker} status={blocker} tone={currentBlockerTone(detail, copy)} rows={currentBlockerRows(detail, copy)} />
      <NextActionCard actions={actions} busy={busy} copy={copy} detail={detail} />
      <CommandSummaryCard title={copy.labels.topologySummary} status={topology.status} tone={UI_TONE.Neutral} rows={topology.rows} />
    </div>
  );
}

export function ActiveTaskWorkspace({ actions, busy, copy, detail, rootCause }: IncidentCommandSurfaceProps & {
  readonly rootCause: string;
}): ReactNode {
  const projection = activeTaskProjection(detail, rootCause, copy);
  if (!projection) {
    return null;
  }
  const { task, owner } = projection;
  const ownerLabel = owner ? owner.label : actorRefLabel(task.ownerResponderRef, copy);
  const invokeTask = () => {
    if (task.gateId === CLOSEOUT_GATE.ApprovalQueue) {
      void actions.transitionApproval(IncidentApprovalTransition.Approve);
      return;
    }
    if (task.gateId === CLOSEOUT_GATE.OwnerAcknowledgement) {
      void actions.transitionApproval(IncidentApprovalTransition.OwnerAcknowledge);
      return;
    }
    void actions.recordReadinessGate(task.gateId);
  };
  return (
    <section className={`active-task-workspace ${projection.gateTone}`} aria-label={copy.labels.activeTask}>
      <div className="active-task-copy">
        <SectionHeader title={copy.labels.activeTask} status={statusLabel(copy, task.status)} tone={projection.gateTone} />
        <h3>{task.title}</h3>
        <p>{task.summary}</p>
        <dl className="active-task-facts">
          <CommandStripItem label={copy.labels.activeResponder} value={ownerLabel} />
          <CommandStripItem label={copy.labels.role} value={owner?.roleLabel ?? statusLabel(copy, task.responderRole)} />
          <CommandStripItem label={copy.labels.authority} value={statusLabel(copy, task.authorityClass)} />
          <CommandStripItem label={copy.labels.requiredEvidence} value={task.evidencePrompt} />
        </dl>
      </div>
      <div className="active-task-action">
        <StatusPill label={owner?.acknowledgementLabel ?? statusLabel(copy, task.primaryCtaKind)} tone={owner?.tone ?? projection.gateTone} />
        <Button className="active-task-button" disabled={busy} type={BUTTON_TYPE.Button} variant="primary" onClick={invokeTask}>
          {task.primaryCta}
        </Button>
      </div>
    </section>
  );
}

export function ResponderStrip({ copy, detail, rootCause }: {
  readonly copy: OpsCenterCopy;
  readonly detail: IncidentDetail;
  readonly rootCause: string;
}): ReactNode {
  const rows = responderRows(detail, rootCause, copy);
  const activeCount = rows.filter((row) => row.assignedTaskId).length;
  return (
    <section className="responder-strip" aria-label={copy.labels.responders}>
      <SectionHeader title={copy.labels.responders} status={`${activeCount}/${rows.length}`} />
      <div className="responder-strip-grid">
        {rows.slice(0, 8).map((row) => <ResponderCard copy={copy} key={row.id} row={row} />)}
      </div>
    </section>
  );
}

export function IncidentLifecycleStrip({ copy, detail }: {
  readonly copy: OpsCenterCopy;
  readonly detail: IncidentDetail;
}): ReactNode {
  const rows = lifecycleRows(detail, copy);
  const lastTransition = rows.at(-1)?.[1] ?? copy.labels.notRecorded;
  const recordedRows = rows.slice(0, -1).filter(([, value]) => value !== copy.labels.notRecorded);
  const visibleRows = (recordedRows.length > 0 ? recordedRows : rows.slice(0, 2)).slice(0, 4);
  return (
    <article className="command-lifecycle-strip">
      <SectionHeader title={copy.labels.lifecycle} status={lastTransition} tone={UI_TONE.Neutral} />
      <div className="command-lifecycle-grid">
        {visibleRows.map(([label, value]) => (
          <div key={label} className="command-lifecycle-entry">
            <span>{label}</span>
            <strong>{value}</strong>
          </div>
        ))}
      </div>
    </article>
  );
}

function ResponderCard({ copy, row }: { readonly copy: OpsCenterCopy; readonly row: ResponderRow }): ReactNode {
  return (
    <article className={`responder-card ${row.tone}`}>
      <div className="responder-card-top">
        <div>
          <strong>{row.label}</strong>
          <span>{row.roleLabel}</span>
        </div>
        <StatusPill label={statusLabel(copy, row.kind)} tone={responderKindTone(row)} />
      </div>
      <dl className="responder-card-facts">
        <CommandStripItem label={copy.labels.acknowledgement} value={row.acknowledgementLabel} />
        <CommandStripItem label={copy.labels.action} value={row.currentAction} />
      </dl>
    </article>
  );
}

function CommandStripItem({ label, value }: { readonly label: string; readonly value: string }): ReactNode {
  return (
    <div className="command-strip-item">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function CommandSummaryCard({ title, status, tone, rows }: { readonly title: string; readonly status?: string; readonly tone: UiTone; readonly rows: readonly (readonly [string, string])[] }): ReactNode {
  return (
    <article className={`command-summary-card ${tone}`}>
      <SectionHeader title={title} status={status ?? ""} tone={tone} />
      <CommandRows rows={rows} />
    </article>
  );
}

function NextActionCard({ actions, busy, copy, detail }: IncidentCommandSurfaceProps): ReactNode {
  const row = nextCommandAction(detail, copy);
  if (!row) {
    return (
      <CommandSummaryCard
        title={copy.labels.safeNextAction}
        status={copy.labels.actionUnavailable}
        tone={UI_TONE.Neutral}
        rows={[
          [copy.labels.actionState, copy.labels.actionUnavailable] as const,
          [copy.labels.consequence, copy.labels.noActiveBlockers] as const
        ]}
      />
    );
  }
  return (
    <article className={`command-summary-card ${row.tone}`}>
      <SectionHeader title={copy.labels.safeNextAction} status={row.buttonLabel} tone={row.tone} />
      <p className="command-action-summary">{row.summary}</p>
      <CommandRows rows={[
        [copy.labels.actionState, statusLabel(copy, row.status)] as const,
        [copy.labels.consequence, row.policyLabel] as const,
        [copy.labels.owner, actorRefLabel(detail.incident.accountability.currentAssigneeRef, copy)] as const
      ]} />
      <Button className="command-summary-action" disabled={busy} type={BUTTON_TYPE.Button} variant="secondary" onClick={() => void actions.invokeOpsAction(row.id)}>
        {row.buttonLabel}
      </Button>
    </article>
  );
}

function responderKindTone(row: ResponderRow): UiTone {
  if (row.blocked) {
    return UI_TONE.Warn;
  }
  if (row.kind === IncidentResponderKind.Ai || row.kind === IncidentResponderKind.System) {
    return UI_TONE.Ok;
  }
  return UI_TONE.Neutral;
}

function CommandRows({ rows }: { readonly rows: readonly (readonly [string, string])[] }): ReactNode {
  return (
    <dl className="command-row-list">
      {rows.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function currentBlockerRows(detail: IncidentDetail, copy: OpsCenterCopy): readonly (readonly [string, string])[] {
  return [
    [copy.labels.owner, currentBlockerOwner(detail, copy)],
    [copy.labels.actionState, currentBlockerActionState(detail, copy)],
    [copy.labels.consequence, currentBlockerConsequence(detail, copy)]
  ];
}

function topologySummary(detail: IncidentDetail, copy: OpsCenterCopy): { readonly status: string; readonly rows: readonly (readonly [string, string])[] } {
  const graph = impactGraph(detail, copy);
  const routeCount = graph.nodes.filter((node) => node.nodeKind === GRAPH_NODE_KIND.Route).length;
  const providerCount = graph.nodes.filter((node) => node.nodeKind === GRAPH_NODE_KIND.Provider).length;
  const customerCount = graph.nodes.filter((node) => node.nodeKind === GRAPH_NODE_KIND.CustomerSegment).length;
  const firstRoute = graph.nodes.find((node) => node.nodeKind === GRAPH_NODE_KIND.Route)?.label ?? copy.labels.none;
  const firstProvider = graph.nodes.find((node) => node.nodeKind === GRAPH_NODE_KIND.Provider)?.label;
  const firstCustomer = graph.nodes.find((node) => node.nodeKind === GRAPH_NODE_KIND.CustomerSegment)?.label;
  const summaryPath = [serviceLabel(detail.incident.serviceId, copy), firstRoute, firstProvider ?? firstCustomer]
    .filter((value): value is string => Boolean(value && value !== copy.labels.none))
    .join(" -> ") || copy.empty.noneRecorded;
  return {
    status: `${routeCount} ${copy.labels.routes} / ${providerCount} ${copy.sections.tools} / ${customerCount} ${copy.labels.impacted}`,
    rows: [
      [copy.forms.service, serviceLabel(detail.incident.serviceId, copy)],
      [copy.labels.impactedRoutes, firstRoute],
      [copy.labels.connectionPoints, summaryPath]
    ]
  };
}

function nextCommandAction(detail: IncidentDetail, copy: OpsCenterCopy): ActionRow | null {
  const rows = actionRows(detail, copy);
  return rows.find((row) => !row.requiresApproval && row.status !== WORKFLOW_STATUS.Blocked)
    ?? rows.find((row) => row.requiresApproval)
    ?? rows.find((row) => row.endpointKind === OPS_ACTION_ENDPOINT_KIND.CodeBridgeSession)
    ?? rows[0]
    ?? null;
}

function currentBlocker(detail: IncidentDetail, copy: OpsCenterCopy): string {
  if (detail.communicationSummary.outageNoticeState === OUTAGE_NOTICE_STATE.Draft) {
    return copy.labels.outageNoticeApproval;
  }
  if (detail.communicationSummary.deliveryRiskCount > 0) {
    return copy.labels.deliveryRisk;
  }
  if (actionRows(detail, copy).some((row) => row.requiresApproval)) {
    return copy.labels.approvalRequired;
  }
  return copy.labels.noActiveBlockers;
}

function currentBlockerTone(detail: IncidentDetail, copy: OpsCenterCopy): UiTone {
  return currentBlocker(detail, copy) === copy.labels.noActiveBlockers ? UI_TONE.Ok : UI_TONE.Warn;
}

function currentBlockerActionState(detail: IncidentDetail, copy: OpsCenterCopy): string {
  const summary = detail.communicationSummary;
  if (summary.outageNoticeState === OUTAGE_NOTICE_STATE.Draft) {
    return statusLabel(copy, summary.outageNoticeState);
  }
  if (summary.deliveryRiskCount > 0) {
    return `${summary.deliveryRiskCount} ${copy.labels.deliveryRisks}`;
  }
  const approvalCount = actionRows(detail, copy).filter((row) => row.requiresApproval).length;
  return approvalCount ? `${approvalCount} ${copy.sections.approvalQueue}` : copy.status.ready;
}

function currentBlockerConsequence(detail: IncidentDetail, copy: OpsCenterCopy): string {
  const summary = detail.communicationSummary;
  if (summary.outageNoticeState === OUTAGE_NOTICE_STATE.Draft || summary.deliveryRiskCount > 0) {
    return copy.labels.customerCommsRisk;
  }
  if (actionRows(detail, copy).some((row) => row.requiresApproval)) {
    return copy.labels.approvalRequired;
  }
  return copy.labels.noActiveBlockers;
}

function currentBlockerOwner(detail: IncidentDetail, copy: OpsCenterCopy): string {
  const accountability = detail.incident.accountability;
  if (detail.communicationSummary.outageNoticeState === OUTAGE_NOTICE_STATE.Draft || detail.communicationSummary.deliveryRiskCount > 0) {
    return actorRefLabel(accountability.communicationsOwnerRef, copy);
  }
  if (actionRows(detail, copy).some((row) => row.requiresApproval)) {
    return actorRefLabel(accountability.currentAssigneeRef, copy);
  }
  return actorRefLabel(accountability.serviceOwnerRef, copy);
}

function appLabel(serviceId: string): string {
  return humanize(serviceId.split(".")[0] ?? serviceId);
}
